import React, { useRef, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import Container from "../../../components/container/container";
import { numberWithCommas } from "../../../components/helpers";
import PendingOrdersSlider from "../../../components/slider/PendingOrdersSlider";
import { PendingOrdersNav } from "../../../components/slider/PendingOrdersSlider";
import { trackRestaurantOrderAction } from "../../../redux/restaurantOrder/restaurantOrderActions";
import { trackStoreOrderAction } from "../../../redux/storeOrders/storeOrderAction";

const TRACK_TIMEOUT = 10000; // 10s per request — fail fast

export const PendingOrders = () => {
  const ordersRef = useRef(null);
  const dispatch = useDispatch();
  const { myFilteredOrders } = useSelector((state) => state.restaurantOrder);
  const { myFilteredStoreOrders } = useSelector((state) => state.storeOrder);

  // trackingMap: { [orderId]: trackingData | null }
  const [trackingMap, setTrackingMap] = useState({});

  // console.log('myFilteredOrders', myFilteredOrders);
  // console.log('myFilteredStoreOrders', myFilteredStoreOrders);


  // Build the combined list of first 4 from each, immediately from redux
  const pendingOrders = [
    ...(myFilteredOrders?.data?.slice(0, 4) || []).map((o) => ({
      ...o,
      _type: "restaurant",
    })),
    ...(myFilteredStoreOrders?.data?.slice(0, 4) || []).map((o) => ({
      ...o,
      _type: "store",
    })),
  ];

  // Track orders sequentially (one at a time) so we don't blast the server
  const trackSequentially = useCallback(
    async (orders) => {
      let cancelled = false;
      const cancel = () => {
        cancelled = true;
      };

      for (const order of orders) {
        if (cancelled) break;
        try {
          const trackAction =
            order._type === "restaurant"
              ? trackRestaurantOrderAction
              : trackStoreOrderAction;

          const res = await Promise.race([
            dispatch(trackAction({ id: order?.id })),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("timeout")), TRACK_TIMEOUT)
            ),
          ]);

          if (!cancelled) {
            const data = res?.data?.data || res?.data || null;
            setTrackingMap((prev) => ({ ...prev, [order.id]: data }));
          }
        } catch {
          // Timeout or error — skip this order, move to next
          if (!cancelled) {
            setTrackingMap((prev) => ({ ...prev, [order.id]: null }));
          }
        }
      }

      return cancel;
    },
    [dispatch]
  );

  useEffect(() => {
    if (pendingOrders.length === 0) return;

    let cancelFn;
    trackSequentially(pendingOrders).then((cancel) => {
      cancelFn = cancel;
    });

    return () => {
      if (cancelFn) cancelFn();
    };
    // Only re-run when the underlying redux data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myFilteredOrders?.data?.length, myFilteredStoreOrders?.data?.length]);

  if (pendingOrders.length === 0) return null;

  return (
    <Container>
      <div className="py-5 mt-4 sm:mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold capitalize">Pending Orders</h3>
          <div className="flex items-center gap-4">
            <div className="block">
              <PendingOrdersNav ordersRef={ordersRef} />
            </div>
          </div>
        </div>

        <div>
          <PendingOrdersSlider ordersRef={ordersRef}>
            {pendingOrders.map((order) => {
              const tracking = trackingMap[order?.id];
              const courier = tracking?.courier || tracking?.rider;
              const outletName = tracking?.outlet_name || "Order";
              const outletCity = tracking?.outlet_city || "";
              const deliveryCode =
                tracking?.courier_orders?.[0]?.short_code || "";
              const isRestaurant = order._type === "restaurant";
              const orderLink = isRestaurant
                ? `/user/restaurant/orders/${order?.id}`
                : `/user/store/orders/${order?.id}`;
              const isTrackingLoading = !(order?.id in trackingMap);

              return (
                <div
                  className="w-full bg-white dark:bg-darkBg rounded-lg mb-2 relative"
                  key={order?.id}
                >
                  <Link to={orderLink} className="block w-full">
                    <div className="p-5 border dark:border-gray-700 bg-white dark:bg-darkBg rounded-xl">
                      <div className="flex justify-between mb-6">
                        <div>
                          <p className="text-lg font-bold mb-0.5">
                            {outletName} - {outletCity}
                          </p>
                          <p className="text-sm text-gray-500">
                            {moment(order?.created_at).format(
                              "Do MMM, YYYY. h:mm a"
                            )}
                          </p>
                        </div>
                        <h3 className="text-2xl font-semibold">
                          ₦{numberWithCommas(order?.order_amount)}
                        </h3>
                      </div>

                      {isTrackingLoading && (
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                          <p className="text-sm text-gray-400">
                            Loading courier details...
                          </p>
                        </div>
                      )}

                      {courier && (
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold">
                            {courier?.name?.charAt(0) || "C"}
                          </div>
                          <div>
                            <p className="text-sm font-semibold">
                              {courier?.name || "Courier assigned"}
                            </p>
                            {courier?.phone && (
                              <p className="text-xs text-gray-500">
                                {courier?.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {deliveryCode && (
                        <div className="flex justify-between items-center">
                          <div className="flex gap-1">
                            {String(deliveryCode)
                              .split("")
                              .map((digit, i) => (
                                <span
                                  key={i}
                                  className="bg-red-500 text-white w-10 h-10 flex items-center justify-center rounded-full text-lg font-bold"
                                >
                                  {digit}
                                </span>
                              ))}
                          </div>
                          <p className="font-bold text-lg">Delivery Code</p>
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </PendingOrdersSlider>
        </div>
      </div>
    </Container>
  );
};
